import React from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '#components/Icon'
import { Link } from '#components/Link'
import { NavigationItem } from '#components/SideNav/components/NavigationItem'
import { NavigationSection } from '#components/SideNav/components/NavigationSection'
import { useSidebar } from '#hooks/useSidebar'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

export const UserSideNav = ({
  userId,
  userName,
  companyId,
  companyName,
  isSelfView = false,
}: IProps): React.JSX.Element => {
  const { t } = useTranslation()
  const { isCollapsed } = useSidebar()

  const companyBase = `/companies/${companyId}`
  const nestedBase = `${companyBase}/users/${userId}`

  // Self-view: root-level paths. Admin-view: nested company/user paths
  const base = isSelfView ? '' : nestedBase

  return (
    <div className={styles.userNav} data-testid="user-side-nav">
      {!isCollapsed && !isSelfView && (
        <>
          <Link
            href={companyBase}
            className={styles.backLink}
            withUnderline={false}
            ariaLabel={t('Back to {{ companyName }}', { companyName })}
            data-testid="back-to-company"
          >
            <Icon name="arrow-left" width="14" height="14" />
            <span>{t('Back to {{ companyName }}', { companyName })}</span>
          </Link>

          <div className={styles.userHeader}>
            <span className={styles.userLabel}>{t('User')}</span>
            <span className={styles.userDisplayName}>{userName}</span>
          </div>
        </>
      )}

      {!isSelfView && (
        <NavigationSection title={t('Overview')}>
          <NavigationItem label={t('Dashboard')} location={base || '/'} icon="squares" exact />
        </NavigationSection>
      )}

      {isSelfView && <NavigationItem label={t('Dashboard')} location="/" icon="squares" exact />}

      <NavigationSection title={t('Activity')}>
        <NavigationItem label={t('Transactions')} location={`${base}/transactions`} icon="receipt" />
        {isSelfView && <NavigationItem label={t('E-tickets')} location="/my/e-tickets" icon="ticket" />}
        <NavigationItem label={t('Messages')} location={`${base}/messages`} icon="mail" />
      </NavigationSection>

      <NavigationSection title={t('Management')}>
        <NavigationItem label={t('Profile')} location={`${base}/profile`} icon="user" />
        <NavigationItem label={t('Subscription')} location={`${base}/subscription`} icon="clock" />
        <NavigationItem label={t('Card')} location={`${base}/card`} icon="credit-card" />
        <NavigationItem label={t('Budget')} location={`${base}/budget`} icon="receipt" />
        <NavigationItem label={t('Settings')} location={`${base}/settings`} icon="cog" />
      </NavigationSection>
    </div>
  )
}
